import { RightOutlined } from '@ant-design/icons';
import { AmbassadorDetail } from '@omniafishing/core';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AmbassadorProfileMini } from '../../components/ambassador_profile_mini/ambassador_profile_mini';
import { ContentWrapper } from '../../components/content_wrapper/content_wrapper';
import { ScrollToTop } from '../../components/scroll_to_top/scroll_to_top';
import { SEO } from '../../components/seo/seo';
import SvgBlueAmbBadge from '../../components/svg/blue_amb_badge';
import { UserProfileCard } from '../../components/user_profile_card/user_profile_card';
import { DockableVideoPlayer } from '../../components/video_player/dockable_video_player';
import { useResponsive } from '../../hooks/use_responsive';
import { AMBASSADOR_EXCLUDE_ALL, apiV1 } from '../../lib/api';
import { AMBASSADOR_ROLES } from '../../lib/user';
import { RoutePaths } from '../../routes';
import styles from './omnia_blue_landing_page.less';
import {
  bigCards,
  blueAmbassadorSlugs,
  omniaBlueDisclaimer,
  omniaBlueTestimonialQuote,
  omniaBlueValueProp,
  rewardsAndBenefitsCards,
  toolsCards,
} from './omnia_blue_landing_page_data';
import SvgWaveOneDesk from './page_assets/svg_multi_waves/wave_one_desk';
import SvgWaveOneMobile from './page_assets/svg_multi_waves/wave_one_mobile';
import SvgWaveThreeDesk from './page_assets/svg_multi_waves/wave_three_desk';
import SvgWaveThreeMobile from './page_assets/svg_multi_waves/wave_three_mobile';
import SvgWaveTwoDesk from './page_assets/svg_multi_waves/wave_two_desk';
import SvgWaveTwoMobile from './page_assets/svg_multi_waves/wave_two_mobile';
import { OmniaLinkButton } from '../../components/omnia_button/omnia_link_button';

const PHONES_IMAGE =
  'https://omnia-fishing.imgix.net/landing_pages_content/omnia_blue_landing_page/header_phones.png';
const TESTIMONIAL_VID_MUX_ID = 'AA1U00ysTdqR4ldIYeHqL8uK026c2001wlhfo01desKyd14';
const TOOLS_SECTION_GIF =
  'https://omnia-fishing.imgix.net/landing_pages_content/omnia_blue_landing_page/shop_vid.gif';

const OmniaBlueLandingPage = () => {
  const [ambassadors, setAmbassadors] = useState<AmbassadorDetail[]>([]);
  useEffect(() => {
    apiV1
      .ambassadorsFetch({ role: AMBASSADOR_ROLES.BLUE_AMBASSADOR, exclude: AMBASSADOR_EXCLUDE_ALL })
      .then((res) => {
        setAmbassadors(res.data.data);
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  const hasAmbassadors = ambassadors.length > 0;
  const blueAmbassadors =
    hasAmbassadors && ambassadors.filter((amb) => blueAmbassadorSlugs.includes(amb.slug));
  const ambassadorSteve =
    hasAmbassadors && blueAmbassadors.find((amb) => amb.slug === 'steve-rogers');
  const { isDesktop } = useResponsive();
  const mobilePhonesCard = bigCards[0];
  const shoppableVidCard = bigCards[1];

  return (
    <ScrollToTop>
      <SEO title="Omnia Blue" />
      <div className={styles.fullWidthContainer}>
        <ContentWrapper>
          <header>
            <h1 className={styles.fullWidthHeader}>
              Omnia Blue: The Content Platform For Knowledgable Anglers
            </h1>
            <div className={styles.headerWrapper}>
              <div className={styles.textContainer}>
                <h1>Omnia Blue: The Content Platform For Knowledgable Anglers</h1>
                <p>{omniaBlueValueProp}</p>
                <OmniaLinkButton
                  kind="primary"
                  size="lg"
                  fontSize={14}
                  to={RoutePaths.AMBASSADOR_APPLICATION}
                >
                  Get Started & Apply Now
                </OmniaLinkButton>
              </div>
              <img src={PHONES_IMAGE} alt="image of mobile phones" className={styles.phoneImage} />
            </div>
          </header>
        </ContentWrapper>
      </div>
      {isDesktop ? <SvgWaveOneDesk /> : <SvgWaveOneMobile />}
      <ContentWrapper>
        <section className={styles.mainSection}>
          <h2>Your Content, Omnia’s Engaged Community</h2>
          <p>
            Get in front of the perfect audience and make your video and written content work hard
            for you
          </p>
          <div className={styles.bigCardsWrapper}>
            <div className={styles.bigCard}>
              <div className={styles.imgContainer__phones}>
                <img src={mobilePhonesCard.img} alt={mobilePhonesCard.img_alt} />
              </div>
              <h4>{mobilePhonesCard.title}</h4>
              <p>{mobilePhonesCard.text}</p>
            </div>
            <div className={styles.bigCard}>
              <div className={styles.imgContainer__screenShot}>
                <img src={shoppableVidCard.img} alt={shoppableVidCard.img_alt} />
              </div>
              <h4>{shoppableVidCard.title}</h4>
              <p>{shoppableVidCard.text}</p>
            </div>
          </div>
          <h2>Rewards and Benefits</h2>
          <p>We support, recognize, and celebrate creators</p>
          <div className={styles.cardsWrapper}>
            {rewardsAndBenefitsCards.map((card) => {
              return (
                <div className={styles.iconCard} key={card.icon_alt}>
                  <img src={card.icon} alt={card.icon_alt} />
                  <h4>{card.title}</h4>
                  <p>{card.text}</p>
                </div>
              );
            })}
          </div>
        </section>
      </ContentWrapper>
      <section className={styles.testimonials}>
        <div className={styles.testimonialCard}>
          <DockableVideoPlayer
            muxPlaybackId={TESTIMONIAL_VID_MUX_ID}
            dockable={false}
            thumbnailTimeStamp={212}
          />
          <div className={styles.testimonialText}>
            <q>{omniaBlueTestimonialQuote}</q>
            {ambassadorSteve && (
              <div style={{ marginTop: '24px' }}>
                <AmbassadorProfileMini ambassador={ambassadorSteve} />{' '}
              </div>
            )}
          </div>
        </div>
      </section>
      {isDesktop ? (
        <SvgWaveTwoDesk className={styles.waveTwoDesk} />
      ) : (
        <SvgWaveTwoMobile className={styles.waveTwoMobile} />
      )}
      <section>
        <div className={styles.toolsSection}>
          <ContentWrapper>
            <h2>Tools To Help You Create & Grow</h2>
            <p>Omnia provides unique technology and support to help you succeed</p>
            <div className={styles.sectionHeroWrapper}>
              <img src={TOOLS_SECTION_GIF} alt="image of an omnia shoppable video" />
            </div>
            <div className={styles.toolsGrid}>
              {toolsCards.map((card) => (
                <div className={styles.toolCard} key={card.title}>
                  <img src={card.icon} alt={card.icon_alt} className={styles.icon} />
                  <h5>{card.title}</h5>
                  <p>{card.text}</p>
                  <div className={styles.imageWrapper}>
                    <img src={card.img} alt={card.img_alt} className={styles.image} />
                  </div>
                </div>
              ))}
            </div>
          </ContentWrapper>
        </div>
        {isDesktop ? (
          <SvgWaveThreeDesk className={styles.waveThreeDesk} />
        ) : (
          <SvgWaveThreeMobile className={styles.waveThreeMobile} />
        )}
      </section>
      <ContentWrapper>
        <section className={styles.blueAmbassadorsSection}>
          <h2>Omnia Blue Ambassadors</h2>
          <span className={styles.ambassadorLink}>
            <Link to={RoutePaths.AMBASSADORS}> See all ambassadors</Link>
            <RightOutlined />
          </span>
          <div className={styles.blueAmbassadors}>
            <ul className={styles.ambassadorList}>
              {blueAmbassadors.length > 0 &&
                blueAmbassadors.map((ambassador) => (
                  <li className={styles.ambassadorListItem} key={ambassador.slug}>
                    <UserProfileCard
                      profile={ambassador}
                      displayRole={false}
                      className={styles.userProfileCard}
                    />
                  </li>
                ))}
            </ul>
          </div>
          <p>{omniaBlueDisclaimer}</p>
          <p>
            We are in search of content creators big and small, whether you have broad knowledge of
            fishing or a highly specialized area of expertise.{' '}
            <Link to={RoutePaths.AMBASSADOR_APPLICATION}>Apply to join Omnia Blue</Link> now to get
            started and make your voice heard in the fishing industry.
          </p>
        </section>
      </ContentWrapper>
      <section className={styles.bannerCta}>
        <div className={styles.bannerCard}>
          <div className={styles.bannerCard__blue}>
            <SvgBlueAmbBadge />
            <h4>Omnia Blue</h4>
          </div>
          <div className={styles.bannerCard__white}>
            <p>
              Want to help build the future of online tackle shopping? Become an ambassador now and
              work your way up to Blue.
            </p>
          </div>
          <div className={styles.bannerCard__button}>
            <OmniaLinkButton
              kind="primary"
              size="lg"
              fontSize={14}
              to={RoutePaths.AMBASSADOR_APPLICATION}
            >
              Get Started & Apply Now
            </OmniaLinkButton>
          </div>
        </div>
      </section>
    </ScrollToTop>
  );
};

export default OmniaBlueLandingPage;
